import React, { useCallback, useEffect, useState } from "react";
import { Navigate, useNavigate } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { useDispatch } from "react-redux";
import {
  Box,
  Card,
  CardContent,
  Typography,
  Switch,
  FormControlLabel,
  Button,
} from "@mui/material";
import { logout } from "../actions/AdminAction";

const API_BASE = (process.env.REACT_APP_API_URL || "/api").replace(/\/+$/, "");
const STATUS_URL = `${API_BASE}/settings/status`;
const ON_URL = `${API_BASE}/settings/lightsOFF`;
const OFF_URL = `${API_BASE}/settings/lightsON`;

const HangMode = () => {
  const [enabled, setEnabled] = useState(false);
  const [loading, setLoading] = useState(false);

  const dispatch = useDispatch();
  const navigate = useNavigate();

  const adminInfo = JSON.parse(localStorage.getItem("adminInfo") || "{}");

  // Only super admins can access hang mode
  const canAccessHangMode = adminInfo?.role?.isSuperAdmin === true;

  const fetchStatus = useCallback(async () => {
    if (!canAccessHangMode) return;
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${adminInfo.token}`,
        },
      };
      const { data } = await axios.get(STATUS_URL, {
        ...config,
        timeout: 5000,
      });
      setEnabled(Boolean(data?.maintenanceMode));
    } catch {
      setEnabled(false);
    }
  }, [canAccessHangMode, adminInfo.token]);

  useEffect(() => {
    fetchStatus();
  }, [fetchStatus]);

  const handleToggle = async (event) => {
    const shouldEnable = event.target.checked;
    setLoading(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${adminInfo.token}`,
        },
      };

      if (shouldEnable) {
        await axios.post(
          ON_URL,
          {},
          {
            ...config,
            timeout: 5000,
          },
        );
        setEnabled(true);
        toast.success("Hang mode ON");
      } else {
        await axios.post(
          OFF_URL,
          {},
          {
            ...config,
            timeout: 5000,
          },
        );
        setEnabled(false);
        toast.success("Hang mode OFF");
      }
    } catch (err) {
      const msg = err.response?.data?.message || "Failed to toggle";
      toast.error(msg);
      // Revert the switch visual state if the request failed
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/login");
  };

  // Redirect after all hooks have been called
  if (!canAccessHangMode) {
    return <Navigate to="/login" replace />;
  }

  return (
    <Box
      sx={{
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        minHeight: "80vh",
        bgcolor: "var(--cm-bg)",
      }}
    >
      <Card sx={{ minWidth: 320, boxShadow: 3, borderRadius: 2 }}>
        <CardContent
          sx={{
            textAlign: "center",
            py: 4,
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            gap: 2,
          }}
        >
          <Typography
            variant="h5"
            component="div"
            sx={{ fontWeight: "bold", color: "var(--cm-text)" }}
          >
            Hang Mode Control
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Show a server-down screen to every user while keeping super-admin access available.
          </Typography>

          <FormControlLabel
            control={
              <Switch
                checked={enabled}
                onChange={handleToggle}
                disabled={loading}
                color="error"
                sx={{
                  "& .MuiSwitch-switchBase.Mui-checked": {
                    color: "var(--cm-danger)",
                  },
                  "& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track": {
                    backgroundColor: "var(--cm-danger)",
                  },
                }}
              />
            }
            label={
              <Typography
                sx={{
                  fontWeight: "medium",
                  color: enabled ? "var(--cm-danger)" : "text.primary",
                }}
              >
                {enabled ? "ACTIVE" : "INACTIVE"}
              </Typography>
            }
          />

          {loading && (
            <Typography variant="caption" color="text.secondary">
              Processing...
            </Typography>
          )}

          <Button
            variant="outlined"
            color="primary"
            onClick={handleLogout}
            disabled={enabled || loading}
            sx={{ mt: 2 }}
          >
            Logout
          </Button>
          {enabled && (
            <Typography variant="caption" color="error" sx={{ mt: 1 }}>
              Turn off Hang Mode to logout
            </Typography>
          )}
        </CardContent>
      </Card>
    </Box>
  );
};

export default HangMode;
